// WizardProgressIndicator component
// Reusable progress indicator for multi-step wizards
// CHANGE: Created for mission creation wizard - Phase 2 Enhancement
// Shows step numbers, titles, and completion status with visual feedback

import { Check } from 'lucide-react';

export interface WizardStep {
  number: number;
  title: string;
  completed: boolean;
}

interface WizardProgressIndicatorProps {
  steps: WizardStep[];
  currentStep: number;
  onStepClick?: (stepNumber: number) => void;
}

export function WizardProgressIndicator({ steps, currentStep, onStepClick }: WizardProgressIndicatorProps) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between relative">
        {/* Connecting line */}
        <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-200 -z-10" />

        {steps.map((step, index) => {
          const isActive = step.number === currentStep;
          const isCompleted = step.completed;
          const isClickable = isCompleted && onStepClick && step.number < currentStep;

          return (
            <div key={step.number} className="flex flex-col items-center flex-1">
              {/* Circle with number or check */}
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.number)}
                disabled={!isClickable}
                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                  isCompleted
                    ? 'bg-green-500 text-white shadow-md'
                    : isActive
                    ? 'bg-primary-600 text-white shadow-lg ring-4 ring-primary-100'
                    : 'bg-gray-200 text-gray-500'
                } ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
              >
                {isCompleted ? <Check size={20} /> : step.number}
              </button>

              {/* Step title */}
              <div
                className={`mt-2 text-xs font-medium text-center transition-colors ${
                  isActive ? 'text-primary-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}
              >
                {step.title}
              </div>

              {/* Chevron between steps (except last) */}
              {index < steps.length - 1 && (
                <div className="absolute left-1/2 top-5 transform -translate-y-1/2" style={{ left: `${((index + 1) / steps.length) * 100}%` }}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-gray-300">
                    <path d="M7 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
